<script setup lang="ts" generic="T">
import MoreIcon from '@/assets/icons/MoreIcon.vue';
import PositionablePopover from '@/core/dialogs/PositionablePopover.vue';
import DropdownListActions from '@/core/elements/DropdownListActions.vue';
import { IDropdownListAction } from '@/interfaces';
import { toRefs } from '@vueuse/core';
import { EPosition } from 'common/enum/misc.enum';
import { computed, ref, useTemplateRef } from 'vue';

const emit = defineEmits<{
  show: [anchor: HTMLElement];
}>();

const props = withDefaults(
  defineProps<{
    actions: IDropdownListAction<T>[];
    data?: T;
    disabled?: boolean;
    minWidth?: number;
    contentStyle?: string;
    position?: EPosition;
    size?: 'sm' | 'md';
    theme?: 'default' | 'note' | 'secondary-filled';
  }>(),
  {
    data: undefined,
    minWidth: undefined,
    contentStyle: undefined,
    position: undefined,
    size: 'md',
    theme: 'default',
  },
);
const { actions } = toRefs(props);
const actionData = computed(() => props.data as T | undefined);

const dialogRef = useTemplateRef('dialogRef');
const buttonRef = useTemplateRef('buttonRef');
const menuVisible = ref(false);

const hide = (): void => {
  menuVisible.value = false;
  dialogRef.value?.hide();
};

const toggleDialog = (): void => {
  if (dialogRef.value?.isOpen) {
    hide();
    return;
  }
  menuVisible.value = true;
  if (buttonRef.value) emit('show', buttonRef.value);
  dialogRef.value?.show();
};
</script>

<template>
  <div class="relative flex items-center text-secondary">
    <button
      ref="buttonRef"
      type="button"
      :disabled="disabled"
      :class="[
        'outline-inner state-effects hover:bg-tint/10 shrink-0',
        'rounded-full size-button',
        'flex items-center justify-center',
        size === 'sm' && 'size-icon',
        theme === 'note' && 'note-state-effects',
        theme === 'secondary-filled' && 'bg-surface-secondary !text-primary',
        disabled && 'opacity-50 pointer-events-none',
      ]"
      @click.stop="toggleDialog"
    >
      <MoreIcon class="size-icon select-none"></MoreIcon>
    </button>
    <PositionablePopover
      ref="dialogRef"
      :position="position"
      :anchor-element="buttonRef"
      :content-style="contentStyle"
      :offset-x="3"
      flip-strategy="vertical"
      @hide="menuVisible = false"
    >
      <div
        :class="['flex flex-col', 'overflow-hidden', 'p-sm']"
        :style="{ minWidth: minWidth ? `${minWidth}px` : undefined }"
      >
        <!-- @vue-generic {T} -->
        <DropdownListActions :actions="actions" :data="actionData" @click:option="hide" />
      </div>
    </PositionablePopover>
  </div>
</template>
